import {useEffect, useRef, useState} from 'react';
import {Button, Modal, Spinner} from 'react-bootstrap';
import {FaDownload, FaEye, FaFilePdf, FaImage, FaVideo} from 'react-icons/fa';
import {createAttachmentDownload, downloadChatAttachment, loadChatAttachmentBlob, openChatAttachment} from '../../../service/ChatService';
import {chatErrorMessage} from '../chatUtils';
import {formatAttachmentSize, isPreviewableAttachment} from './attachmentRules';

function AttachmentIcon({contentType}) {
    return contentType === 'application/pdf' ? <FaFilePdf aria-hidden="true" /> : contentType.startsWith('video/') ? <FaVideo aria-hidden="true"/> : <FaImage aria-hidden="true" />;
}

export default function MessageAttachments({attachments = []}) {
    const [preview, setPreview] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [workingId, setWorkingId] = useState(null);
    const [error, setError] = useState(null);
    const previewControllerRef = useRef(null);

    function closePreview() {
        previewControllerRef.current?.abort();
        previewControllerRef.current = null;
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreview(null);
        setPreviewUrl(null);
        setPreviewLoading(false);
    }

    async function openPreview(attachment) {
        closePreview();
        const controller = new AbortController();
        previewControllerRef.current = controller;
        setPreview(attachment);
        setPreviewLoading(true);
        setError(null);
        try {
            if (attachment.content_type.startsWith('video/') && !attachment.local_file) {
                const allocation = await createAttachmentDownload(attachment, controller.signal);
                if (!controller.signal.aborted) setPreviewUrl(allocation.download_url);
                return;
            }
            const blob = attachment.local_file || await loadChatAttachmentBlob(attachment, controller.signal);
            if (!controller.signal.aborted) setPreviewUrl(URL.createObjectURL(blob));
        } catch (loadError) {
            if (loadError?.name !== 'AbortError') setError(loadError);
        } finally {
            if (!controller.signal.aborted) setPreviewLoading(false);
        }
    }

    async function download(attachment) {
        setWorkingId(attachment.id);
        setError(null);
        try {
            await downloadChatAttachment(attachment);
        } catch (downloadError) {
            setError(downloadError);
        } finally {
            setWorkingId(null);
        }
    }

    async function openAttachment(attachment) {
        setWorkingId(attachment.id);
        setError(null);
        try {
            await openChatAttachment(attachment);
        } catch (openError) {
            setError(openError);
        } finally {
            setWorkingId(null);
        }
    }

    useEffect(() => () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    useEffect(() => () => previewControllerRef.current?.abort(), []);

    if (attachments.length === 0) return null;
    return (
        <>
            <div className="chat-attachments" aria-label="Message attachments">
                {attachments.map(attachment => (
                    <div className="chat-attachment" key={attachment.id}>
                        <AttachmentIcon contentType={attachment.content_type} />
                        <div>
                            <strong title={attachment.original_filename}>{attachment.original_filename}</strong>
                            <span>{formatAttachmentSize(attachment.size_bytes)}</span>
                        </div>
                        {isPreviewableAttachment(attachment) ? (
                            <button type="button" aria-label={`Preview ${attachment.original_filename}`} onClick={() => openPreview(attachment)}><FaEye /></button>
                        ) : null}
                        {attachment.content_type === 'application/pdf' ? (
                            <button type="button" aria-label={`Open ${attachment.original_filename}`} disabled={workingId === attachment.id} onClick={() => openAttachment(attachment)}><FaEye /></button>
                        ) : null}
                        <button type="button" aria-label={`Download ${attachment.original_filename}`} disabled={workingId === attachment.id} onClick={() => download(attachment)}>
                            {workingId === attachment.id ? <Spinner size="sm" /> : <FaDownload />}
                        </button>
                    </div>
                ))}
            </div>
            {error ? <div className="chat-message-error">{chatErrorMessage(error)}</div> : null}
            {preview ? (
                <Modal show centered size="lg" onHide={closePreview}>
                    <Modal.Header closeButton><Modal.Title>{preview.original_filename}</Modal.Title></Modal.Header>
                    <Modal.Body className="chat-attachment-preview">
                        {previewLoading ? <Spinner /> : null}
                        {!previewLoading && previewUrl ? (
                            preview.content_type.startsWith('video/') ? <video src={previewUrl} controls playsInline preload="metadata" style={{maxWidth:'100%', maxHeight:'65vh'}} onError={() => setError(new Error('This browser cannot play this video. Download it to open on your device.'))}/> : <img src={previewUrl} alt={preview.original_filename} />
                        ) : null}
                        {!previewLoading && !previewUrl && error ? <div className="chat-message-error">{chatErrorMessage(error)}</div> : null}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={closePreview}>Close</Button>
                        <Button onClick={() => download(preview)}><FaDownload className="me-2" />Download</Button>
                    </Modal.Footer>
                </Modal>
            ) : null}
        </>
    );
}
